/**
 * Gerar lote de assistência (vira lote de PRODUÇÃO — Plano de Suprimentos D16)
 * e baixar a planilha .xls do otimizador. Pedidos e clientes diferentes
 * podem entrar no mesmo lote (decisão da gestora 2026-07-07 — otimizar o
 * corte junto). Carrega as peças elegíveis das solicitações selecionadas,
 * permite marcar/desmarcar por peça e exige número + descrição do lote.
 *
 * "Gerar lote" e "Baixar planilha" são DOIS botões (pedido da gestora,
 * 2026-10-02): gerar cria o lote e o envia ao PharuCut; a planilha é baixada
 * depois, quando e quantas vezes ela quiser — aqui ou na aba Lotes gerados.
 * O arquivo sai como "NÚMERO - DESCRIÇÃO.xls".
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiDownload } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  baixarXlsLote,
  enviarLoteAoCut,
  gerarLote,
  listarPecasElegiveis,
  nomeDaPlanilhaDoLote,
} from "../../../services/solicitacoes";
import {
  formatMedidas,
  numeroSolicitacaoDaPeca,
  pecaDasSolicitacoes,
  unwrapLista,
} from "./helpers";
import {
  Checkbox,
  ErrorMsg,
  Field,
  FitaDot,
  FitaWrap,
  GhostBtn,
  InlineError,
  Input,
  Label,
  ListaHeadRow,
  LoteFormGrid,
  PecaRow,
  PecaRowMain,
  RowEnd,
} from "./styles";
import { Carregando, Vazio } from "../_shared/Estados";

function FitasMini({ peca }) {
  return (
    <FitaWrap>
      <FitaDot $on={!!peca.fita_frente} title="Fita frente">F</FitaDot>
      <FitaDot $on={!!peca.fita_traseira} title="Fita traseira">T</FitaDot>
      <FitaDot $on={!!peca.fita_le} title="Fita lado esquerdo">LE</FitaDot>
      <FitaDot $on={!!peca.fita_ld} title="Fita lado direito">LD</FitaDot>
    </FitaWrap>
  );
}

export default function GerarLoteModal({
  pedidosSelecionados = [],
  clienteNome,
  solicitacoes,
  onClose,
  onSuccess,
}) {
  const { request } = useAuth();
  const toast = useToast();

  const [pecas, setPecas] = useState(null);
  const [erro, setErro] = useState(null);
  const [marcadas, setMarcadas] = useState(() => new Set());
  const [numero, setNumero] = useState("");
  // A descrição não repete o número: ele entra na frente ao nomear a planilha.
  const [nome, setNome] = useState(
    pedidosSelecionados.length === 1
      ? `${(clienteNome || "CLIENTE").toUpperCase()} - L?`
      : "LOTE MISTO - L?",
  );
  const [erroForm, setErroForm] = useState("");
  // Depois de gerar, o painel mostra o lote e oferece a planilha.
  const [gerado, setGerado] = useState(null);
  const [avisoDoCut, setAvisoDoCut] = useState("");

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const payload = await listarPecasElegiveis(request);
        if (!ativo) return;
        const lista = unwrapLista(payload).filter((p) =>
          pecaDasSolicitacoes(p, solicitacoes),
        );
        setPecas(lista);
        setMarcadas(new Set(lista.map((p) => p.id)));
      } catch (e) {
        if (ativo) setErro(e?.message || "Erro ao carregar as peças elegíveis.");
      }
    })();
    return () => {
      ativo = false;
    };
    // solicitacoes é estável durante a vida do modal (fecha ao mudar seleção).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const todasMarcadas = useMemo(
    () => !!pecas?.length && pecas.every((p) => marcadas.has(p.id)),
    [pecas, marcadas],
  );

  const togglePeca = (p) => {
    setMarcadas((prev) => {
      const prox = new Set(prev);
      if (prox.has(p.id)) prox.delete(p.id);
      else prox.add(p.id);
      return prox;
    });
    setErroForm("");
  };

  const toggleTodas = () => {
    setMarcadas(todasMarcadas ? new Set() : new Set((pecas || []).map((p) => p.id)));
    setErroForm("");
  };

  const confirmar = async () => {
    const num = numero.trim();
    const nm = nome.trim();
    const pecaIds = Array.from(marcadas);
    if (!num) {
      setErroForm("Informe o número do lote (ex.: 490).");
      return;
    }
    if (!nm) {
      setErroForm("Informe a descrição do lote (ex.: CLIENTE EXEMPLO - L1).");
      return;
    }
    if (pecaIds.length === 0) {
      setErroForm("Marque ao menos uma peça para compor o lote.");
      return;
    }
    setErroForm("");

    let lote;
    try {
      lote = await gerarLote(request, { numero: num, nome: nm, pecaIds });
    } catch (e) {
      // Número repetido (aqui ou no PharuCut) e afins: fica no rodapé, ao
      // lado do botão, e o que foi preenchido não se perde.
      setErroForm(e?.message || "Não deu para gerar o lote.");
      throw e;
    }
    toast.success(`Lote ${lote?.numero || num} gerado.`);
    setAvisoDoCut(lote?.aviso_do_cut || "");
    setGerado(lote);
  };

  const baixar = async () => {
    await baixarXlsLote(gerado);
  };

  const reenviarAoCut = async () => {
    const atualizado = await enviarLoteAoCut(request, gerado.id);
    setGerado(atualizado);
    setAvisoDoCut("");
  };

  // Concluir passa o lote adiante: o container abre os comprovantes das
  // solicitações do lote (baixar JPG/PDF ou imprimir).
  const concluir = () => onSuccess(gerado);

  return (
    <SlideOverPanel
      open
      onClose={gerado ? concluir : onClose}
      title={gerado ? `Lote ${gerado.numero} gerado` : "Gerar lote de produção"}
      subtitle={
        pedidosSelecionados.length > 1
          ? `${pedidosSelecionados.length} pedidos (${pedidosSelecionados.join(", ")})`
          : `Pedido ${pedidosSelecionados[0] || "—"} · ${clienteNome || "sem cliente"}`
      }
      width="min(96vw, 760px)"
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {erroForm && <InlineError>{erroForm}</InlineError>}
          {gerado ? (
            <RowEnd>
              <GhostBtn type="button" onClick={concluir}>
                Concluir
              </GhostBtn>
              <AsyncButton
                variant="primary"
                successMessage="Planilha baixada."
                errorMessage="O download falhou — tente de novo ou baixe na aba Lotes gerados."
                onClick={baixar}
              >
                <FiDownload size={14} /> Baixar planilha
              </AsyncButton>
            </RowEnd>
          ) : (
            <RowEnd>
              <GhostBtn type="button" onClick={onClose}>
                Cancelar
              </GhostBtn>
              <AsyncButton
                variant="primary"
                silentError
                silentSuccess
                disabled={!pecas || pecas.length === 0}
                onClick={confirmar}
              >
                Gerar lote ({marcadas.size}{" "}
                {marcadas.size === 1 ? "peça" : "peças"})
              </AsyncButton>
            </RowEnd>
          )}
        </div>
      }
    >
      {gerado && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <strong>
              {gerado.numero} — {gerado.nome}
            </strong>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {gerado.pecas_count ?? marcadas.size}{" "}
              {(gerado.pecas_count ?? marcadas.size) === 1 ? "peça" : "peças"} · planilha:{" "}
              {nomeDaPlanilhaDoLote(gerado)}
            </div>
          </div>
          {gerado.cut_batch_id && (
            <div style={{ fontSize: 13 }}>
              Enviado ao PharuCut como lote <strong>{gerado.cut_batch_id}</strong>.
            </div>
          )}
          {gerado.falta_enviar_ao_cut && (
            <InlineError>
              O lote foi gerado, mas não chegou ao PharuCut.
              {avisoDoCut ? ` ${avisoDoCut}` : ""}
              <div style={{ marginTop: 8 }}>
                <AsyncButton
                  variant="secondary"
                  successMessage="Lote enviado ao PharuCut."
                  onClick={reenviarAoCut}
                >
                  Enviar ao PharuCut
                </AsyncButton>
              </div>
            </InlineError>
          )}
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            A planilha pode ser baixada agora ou depois, na aba Lotes gerados.
          </div>
        </div>
      )}
      {!gerado && (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <LoteFormGrid>
          <Field>
            <Label htmlFor="lote-numero">Número do lote (obrigatório)</Label>
            <Input
              id="lote-numero"
              value={numero}
              maxLength={36}
              placeholder="ex.: 490"
              onChange={(e) => {
                setNumero(e.target.value);
                setErroForm("");
              }}
            />
          </Field>
          <Field>
            <Label htmlFor="lote-nome">Descrição do lote (obrigatório)</Label>
            <Input
              id="lote-nome"
              value={nome}
              placeholder="ex.: CLIENTE EXEMPLO - L1"
              onChange={(e) => {
                setNome(e.target.value);
                setErroForm("");
              }}
            />
          </Field>
        </LoteFormGrid>
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: -8 }}>
          O número é o código do lote no PharuCut. A planilha sai como{" "}
          <strong>{nomeDaPlanilhaDoLote({ numero: numero || "NÚMERO", nome: nome || "DESCRIÇÃO" })}</strong>.
        </div>

        {erro && <ErrorMsg>{erro}</ErrorMsg>}

        {!erro && pecas === null && <Carregando />}

        {pecas !== null && pecas.length === 0 && (
          <Vazio titulo="Nenhuma peça MDF elegível" descricao="As solicitações selecionadas não têm peças de MDF para gerar lote." />
        )}

        {!!pecas?.length && (
          <>
            <ListaHeadRow>
              <Checkbox
                checked={todasMarcadas}
                onChange={toggleTodas}
                aria-label="Marcar ou desmarcar todas as peças"
              />
              <span>
                {marcadas.size} de {pecas.length}{" "}
                {pecas.length === 1 ? "peça marcada" : "peças marcadas"}
              </span>
            </ListaHeadRow>

            {pecas.map((p) => {
              const numeroOrigem = numeroSolicitacaoDaPeca(p);
              return (
                <PecaRow key={p.id} $on={marcadas.has(p.id)} onClick={() => togglePeca(p)}>
                  <Checkbox
                    checked={marcadas.has(p.id)}
                    onChange={() => togglePeca(p)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Marcar peça ${p.codigo_peca || p.descricao || p.id}`}
                  />
                  <PecaRowMain>
                    <div className="linha1">
                      {[p.modulo, p.descricao].filter(Boolean).join(" — ") ||
                        p.codigo_peca ||
                        "Peça sem descrição"}
                    </div>
                    <div className="linha2">
                      {formatMedidas(p) && <span>{formatMedidas(p)}</span>}
                      {(p.material_nome || p.material_codigo) && (
                        <span>
                          {[p.material_codigo, p.material_nome].filter(Boolean).join(" — ")}
                        </span>
                      )}
                      <span>Qtd: {p.quantidade ?? 1}</span>
                      {numeroOrigem && <span>{numeroOrigem}</span>}
                      <FitasMini peca={p} />
                    </div>
                  </PecaRowMain>
                </PecaRow>
              );
            })}
          </>
        )}
      </div>
      )}
    </SlideOverPanel>
  );
}
